import type { Except } from 'type-fest';
import { enqueuePlayerEventsSync } from '@/lib/realtime-sync/plugins/player-events';
import type { EventId, Player, PlayerEvent, PlayerId } from '@/schemas';
import {
  PlayerEventSchema,
  PlayerSchema,
  createEventId,
  PlayerId as PlayerIdSchema,
} from '@/schemas';
import { db } from './client';

/**
 * Get all player events, ordered by timestamp
 */
export const getPlayerEvents = async (): Promise<PlayerEvent[]> => {
  const events = await db.playerEvents.orderBy('timestamp').toArray();
  return events.map((event) => PlayerEventSchema.parse(event));
};

/**
 * Get player events for a specific player
 */
export const getPlayerEventsByPlayerId = async (playerId: PlayerId): Promise<PlayerEvent[]> => {
  const id = PlayerIdSchema.parse(playerId);

  const [directEvents, sourceEvents, targetEvents] = await Promise.all([
    db.playerEvents.where('payload.playerId').equals(id).toArray(),
    db.playerEvents.where('payload.sourcePlayerId').equals(id).toArray(),
    db.playerEvents.where('payload.targetPlayerId').equals(id).toArray(),
  ]);

  const deduped = new Map<EventId, PlayerEvent>();

  for (const rawEvent of [...directEvents, ...sourceEvents, ...targetEvents]) {
    const event = PlayerEventSchema.parse(rawEvent);
    deduped.set(event.id, event);
  }

  return [...deduped.values()].toSorted((a, b) => a.timestamp - b.timestamp);
};

/**
 * Add a player event (append-only)
 */
export const addPlayerEvent = async (
  event: Except<PlayerEvent, 'id' | 'timestamp'>,
): Promise<EventId> => {
  const id = createEventId();
  const timestamp = Date.now();

  const fullEvent: PlayerEvent = {
    ...event,
    id,
    timestamp,
  };
  const parsedEvent = PlayerEventSchema.parse(fullEvent);

  await db.playerEvents.add(parsedEvent);

  // Update player cache (handle different event types)
  if (parsedEvent.type === 'PlayerMerged') {
    // For merged events, update both source and target players
    await updatePlayerCacheFromEvents(parsedEvent.payload.sourcePlayerId);
    await updatePlayerCacheFromEvents(parsedEvent.payload.targetPlayerId);
  } else {
    // All other events have playerId
    await updatePlayerCacheFromEvents(parsedEvent.payload.playerId);
  }

  if (parsedEvent.userId) {
    enqueuePlayerEventsSync(parsedEvent.userId);
  }
  return id;
};

/**
 * Build Player from events
 * Extracted reusable logic from updatePlayerCacheFromEvents
 */
export const replayPlayerEvents = (events: PlayerEvent[]): Player | undefined => {
  if (events.length === 0) return undefined;

  // Sort events by timestamp to ensure correct ordering
  const sortedEvents = [...events].toSorted((a, b) => a.timestamp - b.timestamp);

  const createdEvent = sortedEvents.find((event) => event.type === 'PlayerCreated');
  if (!createdEvent || createdEvent.type !== 'PlayerCreated') return undefined;

  let player: Player = {
    id: createdEvent.payload.playerId,
    name: createdEvent.payload.name,
    nickname: createdEvent.payload.nickname,
    handle: createdEvent.payload.handle,
    color: createdEvent.payload.color,
    createdBy: createdEvent.userId ?? undefined, // Creator from PlayerCreated event
    userId: undefined, // Only set by PlayerClaimed event, not from creation
    createdAt: createdEvent.timestamp,
    updatedAt: createdEvent.timestamp,
    deletedAt: undefined,
  };

  for (const event of sortedEvents) {
    switch (event.type) {
      case 'PlayerCreated': {
        // Already handled in initial setup, skip here
        break;
      }

      case 'PlayerUpdated': {
        player = {
          ...player,
          name: event.payload.name ?? player.name,
          nickname: event.payload.nickname ?? player.nickname,
          handle: event.payload.handle ?? player.handle,
          color: event.payload.color ?? player.color,
          updatedAt: event.timestamp,
        };
        break;
      }

      case 'PlayerDeleted': {
        player = {
          ...player,
          deletedAt: event.timestamp,
          updatedAt: event.timestamp,
        };
        break;
      }

      case 'PlayerUnmerged':
      case 'PlayerRestored': {
        // Player restored (either from merge unmerge or delete restore)
        player = {
          ...player,
          deletedAt: undefined,
          updatedAt: event.timestamp,
        };
        break;
      }

      case 'PlayerClaimed': {
        // Associate player with user (claimee)
        // createdBy remains unchanged (set only from PlayerCreated event)
        player = {
          ...player,
          userId: event.userId ?? undefined,
          handle: event.payload.handle ?? player.handle, // Set handle when claiming
          updatedAt: event.timestamp,
        };
        break;
      }

      case 'PlayerUnclaimed': {
        // Disassociate player from user (claimee)
        // createdBy remains unchanged
        // Clear handle when unclaiming (handles are only for claimed players)
        player = {
          ...player,
          userId: undefined,
          handle: undefined, // Clear handle when unclaiming
          updatedAt: event.timestamp,
        };
        break;
      }

      default: {
        // For events that don't explicitly handle userId, update from event.userId
        // (for backward compatibility with old events)
        // Note: createdBy should never be updated here, only userId
        if (event.userId) {
          player = {
            ...player,
            userId: event.userId,
          };
        }
        break;
      }
    }
  }

  return PlayerSchema.parse(player);
};

/**
 * Update player cache from events
 * Called after adding a player event
 */
export const updatePlayerCacheFromEvents = async (playerId: PlayerId): Promise<void> => {
  const events = await getPlayerEventsByPlayerId(playerId);
  const player = replayPlayerEvents(events);

  if (!player) return;

  await db.players.put(player);
};

type PlayerMergedEvent = Extract<PlayerEvent, { type: 'PlayerMerged' }>;
type PlayerUnmergedEvent = Extract<PlayerEvent, { type: 'PlayerUnmerged' }>;

const isPlayerMergedEvent = (event: PlayerEvent): event is PlayerMergedEvent => {
  return event.type === 'PlayerMerged';
};

const isPlayerUnmergedEvent = (event: PlayerEvent): event is PlayerUnmergedEvent => {
  return event.type === 'PlayerUnmerged';
};

export type ActivePlayerMerge = {
  mergeEvent: PlayerMergedEvent;
  sourcePlayer: Player;
};

const buildUnmergeLookup = (
  unmergeEvents: PlayerUnmergedEvent[],
): Map<PlayerId, PlayerUnmergedEvent[]> => {
  const lookup = new Map<PlayerId, PlayerUnmergedEvent[]>();
  for (const event of unmergeEvents) {
    const existing = lookup.get(event.payload.playerId);
    if (existing) {
      existing.push(event);
      existing.sort((a, b) => a.timestamp - b.timestamp);
    } else {
      lookup.set(event.payload.playerId, [event]);
    }
  }
  return lookup;
};

const fetchPlayerForMerge = async (playerId: PlayerId): Promise<Player | undefined> => {
  const events = await getPlayerEventsByPlayerId(playerId);
  return replayPlayerEvents(events);
};

export const getActivePlayerMergeRecords = async (): Promise<ActivePlayerMerge[]> => {
  const mergeEventsRaw = await db.playerEvents.where('type').equals('PlayerMerged').toArray();
  const mergeEvents = mergeEventsRaw.filter((event): event is PlayerMergedEvent =>
    isPlayerMergedEvent(event),
  );

  if (mergeEvents.length === 0) {
    return [];
  }

  const sourcePlayerIds = [...new Set(mergeEvents.map((event) => event.payload.sourcePlayerId))];

  const unmergeEventsRaw = await db.playerEvents
    .where('payload.playerId')
    .anyOf(sourcePlayerIds)
    .toArray();
  const unmergeEvents = unmergeEventsRaw.filter((event): event is PlayerUnmergedEvent =>
    isPlayerUnmergedEvent(event),
  );
  const unmergeLookup = buildUnmergeLookup(unmergeEvents);

  const sourcePlayerEntries = await Promise.all(
    sourcePlayerIds.map(async (sourcePlayerId) => {
      const player = await fetchPlayerForMerge(sourcePlayerId);
      return [sourcePlayerId, player] as const;
    }),
  );

  const sourcePlayers = new Map<PlayerId, Player>(
    sourcePlayerEntries.filter((entry): entry is [PlayerId, Player] => entry[1] !== undefined),
  );

  const activeMerges: ActivePlayerMerge[] = [];

  for (const mergeEvent of mergeEvents) {
    const unmergesForSource = unmergeLookup.get(mergeEvent.payload.sourcePlayerId) ?? [];
    const isUndone = unmergesForSource.some(
      (unmergeEvent) => unmergeEvent.timestamp > mergeEvent.timestamp,
    );
    if (isUndone) {
      continue;
    }

    const sourcePlayer = sourcePlayers.get(mergeEvent.payload.sourcePlayerId);
    if (!sourcePlayer) {
      continue;
    }

    activeMerges.push({
      mergeEvent,
      sourcePlayer,
    });
  }

  activeMerges.sort((a, b) => a.mergeEvent.timestamp - b.mergeEvent.timestamp);
  return activeMerges;
};

export const getActivePlayerMergesForTarget = async (
  targetPlayerId: PlayerId,
): Promise<ActivePlayerMerge[]> => {
  const id = PlayerIdSchema.parse(targetPlayerId);
  const activeMerges = await getActivePlayerMergeRecords();
  return activeMerges.filter((merge) => merge.mergeEvent.payload.targetPlayerId === id);
};

export const getActivePlayerMergeTargetIds = async (): Promise<PlayerId[]> => {
  const activeMerges = await getActivePlayerMergeRecords();
  const targetIds = new Set<PlayerId>();
  for (const merge of activeMerges) {
    targetIds.add(merge.mergeEvent.payload.targetPlayerId);
  }
  return [...targetIds];
};
